import React from "react";
import styled from "styled-components";
import Window from "../Window";
import PixelIcon from "../PixelIcon";
import SolitaireCore from "../SolitaireCore";
import { cardBackForGameId } from "../SpectatedSolitaire";

const GAME_WIDTH = 620;
const GAME_HEIGHT = 435;
const SCALE = 0.5;
const ITEM_WIDTH = GAME_WIDTH * SCALE;
const BOARD_HEIGHT = Math.ceil(GAME_HEIGHT * SCALE);
const CONTROL_HEIGHT = 30;
const ITEM_HEIGHT = BOARD_HEIGHT + CONTROL_HEIGHT;
const ROW_GAP = 20;
const TOP_PADDING = 16;
const BOTTOM_PADDING = 16;
const ROW_PITCH = ITEM_HEIGHT + ROW_GAP;
const OVERSCAN_ROWS = 2;
const EMPTY_ID_SET = new Set();
const EMPTY_STATE_MAP = new Map();

const EMPTY_STATE = Object.freeze({
  stock: [],
  waste: [],
  tableau: Array.from({ length: 7 }, () => ({
    faceDown: [],
    faceUp: [],
  })),
  foundations: [[], [], [], []],
  drawSize: 3,
});

// Stable identity for cloneElement props — a fresh array every render
// would defeat SpectatedSolitaire's React.memo.
const PREVIEW_DISABLED_CONTROLS = ["minimize", "maximize", "close"];

const StaticGame = React.memo(function StaticGame({
  gameId,
  state = EMPTY_STATE,
}) {
  return (
    <SolitaireCore
      title={`Game #${gameId} — Solitaire Alone Together`}
      state={state}
      titleIcon="solitaire"
      cardBack={cardBackForGameId(gameId)}
      interactive={false}
      inactive
      controls={["minimize", "maximize", "close"]}
      disabledControls={["minimize", "maximize", "close"]}
      menus={[
        { label: "Game", disabled: true },
        { label: "Help", disabled: true },
      ]}
    />
  );
});

function ActiveGamesWindow({
  gameIds,
  endedGameIds,
  readyGameIds,
  finalStates = EMPTY_STATE_MAP,
  gameSlots,
  watchDisabledIds = EMPTY_ID_SET,
  width,
  height,
  fill = false,
  inactive = false,
  suspended = false,
  onRangeChange,
  onLoadFinal,
  onWatch,
  onMinimize,
  onClose,
}) {
  const scrollerRef = React.useRef(null);
  const gridRef = React.useRef(null);
  const frameRef = React.useRef(null);
  const latestScrollTopRef = React.useRef(0);
  const [viewportHeight, setViewportHeight] = React.useState(
    Math.max(1, (height ?? 435) - 54),
  );
  const [columns, setColumns] = React.useState(1);
  const [scrollTop, setScrollTop] = React.useState(0);

  React.useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;
    const read = () => {
      setViewportHeight(el.clientHeight);
      // CSS Grid owns horizontal layout; reading its resolved track list
      // tells virtualization how many IDs make one row.
      const template = gridRef.current
        ? getComputedStyle(gridRef.current).gridTemplateColumns.trim()
        : "";
      const nextColumns =
        template && template !== "none" ? template.split(/\s+/).length : 1;
      setColumns((prev) => (prev === nextColumns ? prev : nextColumns));
    };
    read();
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  React.useEffect(
    () => () => {
      if (frameRef.current != null) cancelAnimationFrame(frameRef.current);
    },
    [],
  );

  const onScroll = (e) => {
    latestScrollTopRef.current = e.currentTarget.scrollTop;
    if (frameRef.current != null) return;
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null;
      setScrollTop(latestScrollTopRef.current);
    });
  };

  const totalRows = Math.ceil(gameIds.length / columns);
  const firstVisibleRow = Math.floor(
    Math.max(0, scrollTop - TOP_PADDING) / ROW_PITCH,
  );
  const lastVisibleRow = Math.floor(
    Math.max(0, scrollTop + viewportHeight - 1 - TOP_PADDING) / ROW_PITCH,
  );
  const firstRow = Math.max(0, firstVisibleRow - OVERSCAN_ROWS);
  const lastRow = Math.min(
    Math.max(0, totalRows - 1),
    lastVisibleRow + OVERSCAN_ROWS,
  );
  const firstIndex = firstRow * columns;
  const lastIndex = Math.min(gameIds.length, (lastRow + 1) * columns);
  const renderedIds = gameIds.slice(firstIndex, lastIndex);
  // Ended entries stay in the request until their final board arrives.
  // The wire only names an ended id for the celebration join (a win
  // mid-cascade answers with won_elapsed_ms); the final board comes over
  // HTTP below, so an offscreen ending still settles into its real final
  // state rather than a permanently dim empty board.
  const requestedIds = suspended
    ? []
    : renderedIds.filter(
        (id) => !endedGameIds.has(id) || !finalStates.has(id),
      );
  const requestedKey = requestedIds.join(",");

  // Ended stubs in (or near) view fetch their final board from the Past
  // Games endpoint; the hook dedupes and retries the just-ended
  // persistence race.
  const neededFinalsKey = suspended
    ? ""
    : renderedIds
        .filter((id) => endedGameIds.has(id) && !finalStates.has(id))
        .join(",");
  React.useEffect(() => {
    if (!onLoadFinal || neededFinalsKey === "") return;
    for (const raw of neededFinalsKey.split(",")) {
      onLoadFinal(Number(raw));
    }
  }, [neededFinalsKey, onLoadFinal]);

  React.useEffect(() => {
    // A short debounce collapses momentum-scroll churn. Two overscan
    // rows hide the delay while keeping honest use comfortably below
    // the server's independent browse-rate bucket.
    const t = setTimeout(() => onRangeChange(requestedIds), 150);
    return () => clearTimeout(t);
    // requestedKey is the stable wire identity; requestedIds is
    // intentionally rebuilt from the latest roster on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedKey, onRangeChange]);

  const slotById = new Map(gameSlots.map((slot) => [String(slot.key), slot]));
  const activeCount = gameIds.reduce(
    (count, id) => count + (endedGameIds.has(id) ? 0 : 1),
    0,
  );

  return (
    <StyledWindow
      title="All Active Games"
      icon={<PixelIcon name="all-games" size={16} />}
      controls={["minimize", "close"]}
      onControlClick={(control) => {
        if (control === "minimize") onMinimize();
        else if (control === "close") onClose();
      }}
      inactive={inactive}
      data-fill={fill ? "true" : undefined}
      statusFields={[
        activeCount > 0
          ? `${activeCount} other ${activeCount === 1 ? "game" : "games"} in progress`
          : "you're the only one here right now",
      ]}
      style={{
        "--browser-w": fill ? "100%" : `${width}px`,
        "--browser-h": fill ? "100%" : `${height}px`,
      }}
    >
      <Scroller
        ref={scrollerRef}
        onScroll={onScroll}
        data-active-games-scroller
      >
        <Spacer
          style={{
            height: Math.max(
              0,
              TOP_PADDING + totalRows * ROW_PITCH - ROW_GAP + BOTTOM_PADDING,
            ),
          }}
        >
          <GameGrid
            ref={gridRef}
            style={{ "--grid-top": `${TOP_PADDING + firstRow * ROW_PITCH}px` }}
          >
            {renderedIds.map((gameId) => {
              const ended = endedGameIds.has(gameId);
              const slot = slotById.get(String(gameId));
              const finalState = finalStates.get(gameId);
              const loaded = ended
                ? finalState != null
                : readyGameIds.has(gameId) && slot != null;
              const watchable = loaded && !watchDisabledIds.has(gameId);
              return (
                <GameItem
                  key={gameId}
                  data-active-game-id={gameId}
                  data-active-game-loaded={loaded ? "true" : "false"}
                  data-active-game-ended={ended ? "true" : undefined}
                >
                  <Preview data-placeholder={!loaded ? "true" : undefined}>
                    <ScaleBox>
                      {ended && finalState ? (
                        <StaticGame gameId={gameId} state={finalState} />
                      ) : loaded ? (
                        React.cloneElement(slot, {
                          ambientSound: false,
                          disabledControls: PREVIEW_DISABLED_CONTROLS,
                        })
                      ) : (
                        <StaticGame gameId={gameId} />
                      )}
                    </ScaleBox>
                  </Preview>
                  <ControlStrip>
                    <GameLabel>Game #{gameId}</GameLabel>
                    {ended ? (
                      <EndedLabel>Ended</EndedLabel>
                    ) : (
                      <button
                        type="button"
                        aria-label={`Watch Game #${gameId}`}
                        disabled={!watchable}
                        onClick={() => onWatch(gameId)}
                      >
                        Watch
                      </button>
                    )}
                  </ControlStrip>
                </GameItem>
              );
            })}
          </GameGrid>
        </Spacer>
      </Scroller>
    </StyledWindow>
  );
}

const StyledWindow = styled(Window)`
  width: var(--browser-w);
  height: var(--browser-h);
`;

const Scroller = styled.div`
  height: 100%;
  overflow-y: auto;
  background: #808080;
  border: 2px solid;
  border-color: #404040 #fff #fff #404040;
  /* Touch scrolling/long-press must never select the game labels or
     preview boards. */
  -webkit-user-select: none;
  user-select: none;
  -webkit-touch-callout: none;
`;

const Spacer = styled.div`
  position: relative;
  min-height: 100%;
`;

const GameGrid = styled.div`
  position: absolute;
  top: var(--grid-top);
  left: 0;
  width: 100%;
  display: grid;
  grid-template-columns: repeat(auto-fill, ${ITEM_WIDTH}px);
  grid-auto-rows: ${ITEM_HEIGHT}px;
  justify-content: space-around;
  row-gap: ${ROW_GAP}px;
`;

const GameItem = styled.div`
  width: ${ITEM_WIDTH}px;
  height: ${ITEM_HEIGHT}px;
`;

const Preview = styled.div`
  width: ${ITEM_WIDTH}px;
  height: ${BOARD_HEIGHT}px;
  overflow: hidden;
  pointer-events: none;

  &[data-placeholder="true"] {
    filter: grayscale(0.35) brightness(0.62);
    opacity: 0.72;
  }

`;

const ScaleBox = styled.div`
  transform: scale(${SCALE});
  transform-origin: top left;
`;

const ControlStrip = styled.div`
  box-sizing: border-box;
  height: ${CONTROL_HEIGHT}px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 3px 4px;
  background: #c0c0c0;
  border-top: 1px solid #fff;
  box-shadow:
    inset -1px -1px #404040,
    inset 1px 1px #dfdfdf;

  button {
    min-width: 62px;
    padding: 2px 10px;
  }
`;

const GameLabel = styled.span`
  min-width: 0;
  flex: 1 1 auto;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const EndedLabel = styled.span`
  padding: 0 6px;
  color: #404040;
`;

export default ActiveGamesWindow;
